import React from 'react'
import { Button } from 'reactstrap'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'

// Käyttäjät page help: what each role can do. The Finnish, user-facing counterpart of
// docs/roles.md - keep the two in step when role behaviour changes. The developer notes
// in docs/roles.md (file names, quirks) are deliberately left out here.

const ROLE_ORDER = ['admin', 'cashier', 'partner', 'cooperative']

const COMPARISON = [
  {
    label: 'Valikko',
    values: [
      'Kaikki',
      'Kalenteri, Henkilöt, Yritykset, Maksamattomat, Aktiviteetit sekä PikaKassa ja Valot*',
      'Kalenteri',
      'Kalenteri',
    ],
  },
  { label: 'Varausten avaaminen', values: ['Kaikki', 'Kaikki', 'Vain omat', 'Vain omat'] },
  {
    label: 'Muiden varaukset kalenterissa',
    values: ['Näkyvät', 'Näkyvät', 'Näkyvät ilman hintoja', 'Näkyvät vain harmaana, ilman nimeä'],
  },
  { label: 'Hinnat varatessa', values: ['Kaikki', 'Kaikki', 'Vain perushinta', 'Vain perushinta'] },
  { label: 'Maksut ja käsinkorjattu hinta', values: ['Kyllä', 'Kyllä', 'Ei', 'Ei'] },
  {
    label: 'Varauksen peruminen',
    values: ['Kyllä', 'Kyllä', 'Ei vakiovuoroja, ei alle 6 h ennen alkua', 'Ei vakiovuoroja, ei alle 6 h ennen alkua'],
  },
  { label: 'Henkilöt', values: ['Muokkaus', 'Muokkaus', '–', '–'] },
  { label: 'Yritykset', values: ['Muokkaus', 'Vain katselu', '–', '–'] },
  { label: 'Asetukset, laskutus, raportointi', values: ['Kyllä', 'Ei', 'Ei', 'Ei'] },
]

const DESCRIPTIONS = [
  {
    role: 'admin',
    text:
      'Paikan ylläpitäjä. Kaikki Venuemanagerin toiminnot: kalenterit ja varaukset, asiakkaat, ' +
      'laskutus, raportointi, viestintä, tuotteet ja asetukset - myös käyttäjien roolit tällä sivulla.',
  },
  {
    role: 'cashier',
    text:
      'Kassa ja vastaanotto. Kalenteri ja varaukset toimivat kuten adminilla, myös maksujen ' +
      'kirjaus ja peruminen. Henkilöitä voi muokata, yrityksiä vain katsella. Ei pääsyä ' +
      'asetuksiin, laskutukseen eikä raportointiin.',
  },
  {
    role: 'partner',
    text:
      'Yhteistyökumppani, esimerkiksi valmennusyritys, joka varaa vuoroja omalla nimellään. ' +
      'Näkee koko kalenterin, mutta muiden varauksissa ei näy hintoja. Voi varata vain itselleen ' +
      'tai yritykselleen ja vain perushinnalla. Ei näe maksutietoja. Ei voi perua vakiovuoroja ' +
      'eikä varauksia, joiden alkuun on alle 6 tuntia.',
  },
  {
    role: 'cooperative',
    text:
      'Kuten partner, mutta muiden varauksista ei näe mitään: ne näkyvät kalenterissa vain ' +
      'harmaina varattuina aikoina ilman nimeä.',
  },
]

function RolesHelp() {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      {/* Same look as the navbar's Ohjeet button (VideoHelpButton). */}
      <Button size='sm' onClick={() => setOpen(true)}>
        {' '}
        <HelpIcon fontSize='small' /> Roolien erot
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Käyttäjäroolit</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 1 }}>
            Rooli määrää, mitä käyttäjä näkee ja voi tehdä Venuemanagerissa. Anna rooli
            asiakasrekisterissä olevalle henkilölle Anna rooli henkilölle -painikkeella; Uusi käyttäjä
            on henkilölle, jota ei vielä ole rekisterissä. Vaihda rooli käyttäjän rivin painikkeista.
            Punainen X poistaa käyttäjältä pääsyn Venuemanageriin. Käyttäjällä voi olla rooli vain
            yhdessä paikassa, ja uusi rooli tulee voimaan seuraavalla kirjautumisella.
          </Typography>

          <TableContainer sx={{ my: 2 }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell />
                  {ROLE_ORDER.map((role) => (
                    <TableCell key={role} sx={{ fontWeight: 600 }}>
                      {role}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {COMPARISON.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell component='th' scope='row' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {row.label}
                    </TableCell>
                    {row.values.map((value, i) => (
                      <TableCell key={ROLE_ORDER[i]}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
            * PikaKassa ja Valot näkyvät vain, jos ne on otettu paikalla käyttöön.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            <b>Omat varaukset</b> ovat varauksia, joissa maksajana on käyttäjä itse tai yritys, jonka
            yhteyshenkilö tai käyttäjä hän on.
          </Typography>

          {DESCRIPTIONS.map((d) => (
            <div key={d.role} style={{ marginBottom: 12 }}>
              <Typography variant='subtitle2'>{d.role}</Typography>
              <Typography variant='body2'>{d.text}</Typography>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button outline color='primary' size='sm' onClick={() => setOpen(false)}>
            Sulje
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RolesHelp
